import { useEffect, useState } from "react";
import { getEHRTransferStatus } from "../../service/notes.service";
import type { TransferReadiness } from "../../types/index.type";

// Poll cadence for the readiness lanes while the picker is open and the gate
// is still closed. Short-lived: polling stops the moment the gate resolves.
export const READINESS_POLL_INTERVAL_MS = 2500;

// Cap on the execute-gate, measured from modal-open: after 15s the confirm
// button enables regardless — a slow lane must never strand the clinician.
export const READINESS_GATE_CAP_MS = 15_000;

// The execute-gate: confirm waits only while `llm` or `session` is warming.
// `null`/missing readiness gates nothing (backend without the feature ⇒
// today's behavior); a "failed" lane is never surfaced — the button simply
// enables and the transfer takes the inline path server-side.
export const isExecuteGatePending = (readiness?: TransferReadiness | null): boolean => {
  if (!readiness) return false;
  if (readiness.llm === "failed" || readiness.session === "failed") return false;
  return readiness.llm === "pending" || readiness.session === "pending";
};

/**
 * Tracks the execute-gate for the picker's confirm button. While `active` it
 * polls the existing per-note status endpoint (readiness rides that poll)
 * until the gate resolves, the fetch fails (gate nothing), or the cap expires.
 */
export const useTransferReadinessGate = (audioId: number | null | undefined, active: boolean) => {
  const [readiness, setReadiness] = useState<TransferReadiness | null>(null);
  const [capped, setCapped] = useState(false);

  useEffect(() => {
    if (!active || audioId == null) {
      setReadiness(null);
      setCapped(false);
      return;
    }

    let cancelled = false;
    let stopped = false;
    let pollTimer: NodeJS.Timeout | null = null;

    const capTimer = setTimeout(() => {
      if (cancelled) return;
      stopped = true;
      if (pollTimer) clearTimeout(pollTimer);
      setCapped(true);
    }, READINESS_GATE_CAP_MS);

    const tick = async () => {
      try {
        const res = await getEHRTransferStatus(audioId);
        if (cancelled || stopped) return;
        const next: TransferReadiness | null = res.data?.transfer_readiness ?? null;
        setReadiness(next);
        // Gate resolved (ready, failed, or absent) — stop polling.
        if (!isExecuteGatePending(next)) return;
      } catch {
        if (cancelled || stopped) return;
        // A readiness fetch failure must never hold the button — treat it as
        // "no readiness" (gate nothing) and stop.
        setReadiness(null);
        return;
      }
      pollTimer = setTimeout(tick, READINESS_POLL_INTERVAL_MS);
    };
    tick();

    return () => {
      cancelled = true;
      clearTimeout(capTimer);
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [active, audioId]);

  // Gate only on positive knowledge that a lane is pending — before the first
  // poll lands, readiness is null and the button stays enabled.
  const confirmGated = active && !capped && isExecuteGatePending(readiness);
  return { confirmGated, readiness };
};
